/**
 * Trunnion AI - Reasoning Trace Viewer
 * Displays the LLM reasoning chain.
 */

import { Brain, Eye, Zap, Lightbulb, RotateCcw } from 'lucide-react';
import type { ReasoningTraceEntry } from '@/types';

interface ReasoningTraceViewerProps {
  entries: ReasoningTraceEntry[];
}

const traceTypeConfig: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; color: string; label: string }
> = {
  thought: { icon: Brain, color: 'text-blue-400', label: 'Thought' },
  observation: { icon: Eye, color: 'text-green-400', label: 'Observation' },
  action: { icon: Zap, color: 'text-trunnion-gold-500', label: 'Action' },
  plan: { icon: Lightbulb, color: 'text-purple-400', label: 'Plan' },
  reflection: { icon: RotateCcw, color: 'text-cyan-400', label: 'Reflection' },
};

export function ReasoningTraceViewer({ entries }: ReasoningTraceViewerProps) {
  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-trunnion-slate-400">
        No reasoning trace available
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => {
        const config = traceTypeConfig[entry.type] ?? traceTypeConfig.thought;
        const Icon = config.icon;

        return (
          <div
            key={entry.id}
            className="rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/30 p-3"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Icon className={`h-3.5 w-3.5 ${config.color}`} />
              <span className={`text-xs font-semibold uppercase tracking-wider ${config.color}`}>
                {config.label}
              </span>
              <span className="text-xs text-trunnion-slate-500">
                {new Date(entry.timestamp).toLocaleTimeString()}
              </span>
            </div>
            <p className="text-sm text-trunnion-slate-200 whitespace-pre-wrap leading-relaxed">
              {entry.content}
            </p>
          </div>
        );
      })}
    </div>
  );
}
